module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h2', {
    staticClass: "h2"
  }, [_c('a', {
    attrs: {
      "id": "page_12"
    }
  }), _c('a', {
    attrs: {
      "id": "ch1"
    }
  }), _vm._v("ONE")]), _vm._v(" "), _c('h2', {
    staticClass: "h2a"
  }, [_vm._v("From Structure to Task")]), _vm._v(" "), _c('h3', {
    staticClass: "h3a"
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("HISTORICAL EFFECT")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Until the early twentieth century, hermeneutics, the study of understanding and interpretation, had tended to identify itself as a practice in search of the right methods, by means of which it hoped to become something akin to a science. Taking issue with this alignment toward modern scientific methodology, and critiquing the subjectivistic limitations it entails for any nonmethodical truth, Gadamer formulates his philosophical hermeneutics as a description not of the proper technique of understanding – how it "), _c('em', [_vm._v("should")]), _vm._v(" proceed – but of the general ways in which it "), _c('em', [_vm._v("does")]), _vm._v(" proceed. Briefly put, he intends to go “beyond modern science’s concept of method and to think, in fundamental universality, what "), _c('em', [_vm._v("always")]), _vm._v(" occurs.”"), _c('a', {
    attrs: {
      "id": "rch1en1"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1")])]), _vm._v(" This goal, which Gadamer expands beyond mere textual situations and explicitly associates with Kant’s transcendental project, amounts to an account of the a priori conditions of all understanding."), _c('a', {
    attrs: {
      "id": "rch1en2"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Gadamer ultimately concludes that these conditions are limited by the finitude inherent in our historicity and linguisticality. Situated within these conditions, we are subject to their constraining structures as if by “suprasubjective powers,”"), _c('a', {
    attrs: {
      "id": "rch1en3"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en3",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3")])]), _vm._v(" and cannot reach a position in isolation from them. Understanding, considered hermeneutically, is thus always situated. Yet this situation does not amount to a privation of possibility; instead, it is paradoxically precisely within these limitations that all of our potential insights and developments are located. As historical beings, we may not be able to raise ourselves above history to ascertain its course, necessity, or overall purpose, but we have the capacity to understand our history from within, and to always do so differently. In this "), _c('a', {
    attrs: {
      "id": "page_13"
    }
  }), _vm._v("way, we are also generative of the very history that conditions us. This process takes place in the “speculative” medium of language, named so because it mirrors reality in a way that first makes the latter accessible to us, such that we can never reach an initial basis or a final formulation that would exist eternally apart from experience. Our thrownness into language actually frees us from the constraining illusion of univocal meanings and permanent formulations, and frees us up for a discourse that is infinitely iterable.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This discursive-linguistic aspect of Gadamer’s hermeneutics has drawn much attention, not least for its significance in the philosophy of language, but also in its relation to poststructural/deconstructive philosophy, social philosophy, poetics, and the philosophy of art. Gadamer’s own emphasis tends to fall on this aspect of his philosophy, as it represents an intuitive point of access for anyone engaged in communication."), _c('a', {
    attrs: {
      "id": "rch1en4"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en4",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4")])]), _vm._v(" Yet while language may be construed as the basis of Gadamer’s hermeneutic ontology, it is at most a condition equiprimordial with historicity, one structural moment of the same phenomenon without primacy over the other. This equiprimordiality makes it just as difficult to shift in the other direction and view historicity as the more fundamental layer of understanding; one might instead describe historicity as the motor at the core of our experience that always finds “its enactment in the realm of language.”"), _c('a', {
    attrs: {
      "id": "rch1en5"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en5",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5")])]), _vm._v(" Gadamer conceives of this historicity under the heading of historical effect ("), _c('em', [_vm._v("Wirkungsgeschichte")]), _vm._v("), and while the latter could be said to serve as the main principle of his hermeneutics from which all others can be derived,"), _c('a', {
    attrs: {
      "id": "rch1en6"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en6",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6")])]), _vm._v(" it does not stand apart from, nor logically prior to, our involvement in language. Taking over an insight from Heidegger, Gadamer deems it more appropriate to speak of “historicity” or “historicality” ("), _c('em', [_vm._v("Geschichtlichkeit")]), _vm._v(") than “temporality” ("), _c('em', [_vm._v("Zeitlichkeit")]), _vm._v("), since human experience bears the character of occurrence ("), _c('em', [_vm._v("Geschehen")]), _vm._v("), and this is a codeterminant of, rather than simply a temporal container or condition for, linguistic expression."), _c('a', {
    attrs: {
      "id": "rch1en7"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en7",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("7")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("However, even this core principle of historicity contains ambiguities that are not immediately resolvable and that render opaque not only the nature of language, but also the role of the subject situated in history. In fact, it is due to this lack of clarity that historical effect, and thus Gadamer’s position as a whole, has frequently been misconstrued. One of "), _c('a', {
    attrs: {
      "id": "page_14"
    }
  }), _vm._v("these misreadings is that, since he stresses our historical boundedness, Gadamer has been taken to espouse either a form of traditionalism or perspectival relativism. However, this conforms neither to Gadamer’s self-understanding nor to the conceptual foundation of his philosophy. His formulation of an “interplay”"), _c('a', {
    attrs: {
      "id": "rch1en8"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en8",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("8")])]), _vm._v(" ("), _c('em', [_vm._v("Ineinanderspiel")]), _vm._v(") in historicity is intended to eschew several inimical conceptions of history and human historicality, as they all make pernicious assumptions concerning our relation to history, selfhood, and alterity.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The present chapter will begin with a brief outline of these positions. The subsequent analysis of historicity will show how Gadamer responds to and supersedes these stances. The aim is to elucidate the ambiguities at the heart of historical effect, revealing its structure as a situated, intertwined relation and performative enactment, its disclosure of finitude as the “fundamental insight”"), _c('a', {
    attrs: {
      "id": "rch1en9"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("9")])]), _vm._v(" of philosophical hermeneutics, and, by virtue of this insight, its consequences for the broader philosophical issues of social life and ethics. After all, although Gadamer viewed his project as a largely descriptive endeavor, his concepts are not without their intended philosophical repercussions."), _c('a', {
    attrs: {
      "id": "rch1en10"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("10")])]), _vm._v(" Though these are not normative in a strict sense, telling us the steps by which we are supposed to understand, they nevertheless act as a corrective to improper ways of conceiving human historicality, and thus the universal conditions of our understanding. Hermeneutic concepts correct and refine “the way in which constantly exercised understanding understands itself,”"), _c('a', {
    attrs: {
      "id": "rch1en11"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("11")])]), _vm._v(" and thus play a role in cultivating something like social reason. In line with this role, the oneness of horizons that Gadamer takes to underlie historicity turns out to be neither a relativist indifference to strangeness nor an essentialist demand for complete familiarity. Based on its finite and dialogical character, the oneness can instead be considered a form of freedom, operative throughout the present enactment of our inherently historical situation. Yet for the same dialogical reasons, oneness remains an interpretive task, a social and ethical achievement that requires attention and cultivation.")]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("SITUATING HISTORICAL EFFECT")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Gadamer’s concept of historical effect did not arise in a vacuum, nor can it be considered independently of the positions to which it responds. As "), _c('a', {
    attrs: {
      "id": "page_15"
    }
  }), _vm._v("philosophical hermeneutics in general develops out of a long tradition of dealing with interpretive and epistemological issues, Gadamer’s own core concepts are part of the development of historical consciousness from the eighteenth century to the present, and his entire thought bears the mark of intense dialogue with this evolution. One might even say that Gadamer’s elaboration of historical effect is performative and reflexive, precipitated by the very conditions it describes. Philosophical hermeneutics thus stands deeply indebted to a discourse that it nevertheless intends to correct and supplant. To present this discourse in its entirety is obviously beyond the scope of this work."), _c('a', {
    attrs: {
      "id": "rch1en12"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("12")])]), _vm._v(" However, the theme of Gadamer’s philosophical origins remains a fertile ground for debating the place and importance of his thought, and the present work as a whole aspires to contribute to this debate, however slightly."), _c('a', {
    attrs: {
      "id": "rch1en13"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("13")])]), _vm._v(" While I will return below to the social dimension of this discourse, and while subsequent chapters will elaborate this theme, I will here merely identify four prevalent conceptions of historicality that Gadamer takes to be problematic on the basis of two general presuppositions: (1) the elevation to a perspective beyond human historicity and (2) the pretension to immediacy in historical experience. While one pair of historical conceptions is guilty of only the latter assumption, another pair, which Gadamer identifies as historicist, makes both. I will turn to this latter group first.")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('em', [_vm._v("Detached Historicism")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("What Gadamer calls historical consciousness or historicism had arisen as a philosophical view of history during the nineteenth century, through respective pushing and pulling factors."), _c('a', {
    attrs: {
      "id": "rch1en14"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("14")])]), _vm._v(" It can be described as a response to the collapse of teleological-metaphysical explanations of history, be they eschatological or speculative. Thinkers no longer accepted accounts of the goal-directedness of history, but struggled with great difficulty to elaborate an alternative theory. This lack of foundations impelled philosophy to seek its stability elsewhere, to find more plausible descriptions of historical development and knowledge, and thus to account somehow for philosophy’s continued relevance. A great attractor in this pursuit was the burgeoning dominance of natural scientific method, which, with its mechanical-positivist aspirations toward general "), _c('a', {
    attrs: {
      "id": "page_16"
    }
  }), _vm._v("laws, promised philosophy a similarly grounded study of the past, no less than a vindication of its own scientific status. In adopting the naturalist viewpoint that sees time, in spatial terms, as the uniform and infinite container for historical events, historicism was able to treat all points in history as essentially equal, and to survey these points as if from above. Two distinct approaches took shape at this suprahistorical level.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("One, a historically objective approach or “theoretical historicism,” took history to be largely immobile and continuous."), _c('a', {
    attrs: {
      "id": "rch1en15"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en15",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("15")])]), _vm._v(" In the terms of the dominant Neo-Kantian school within this approach, one desired to discover and justify the knowledge-claims contained in history. One thus engaged in a Kantian critique of history, separating contingent facts from historical truths by tracing law-like patterns of meaning. This focus on formal-logical patterns gave rise to a history of problematics ("), _c('em', [_vm._v("Problemgeschichte")]), _vm._v("), which treated the past as the arena of recurring problems (and which Gadamer labels “a bastard of historicism”"), _c('a', {
    attrs: {
      "id": "rch1en16"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en16",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("16")])]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In a programmatic 1910 essay, Nicolai Hartmann proposed a historiography of philosophy founded on "), _c('em', [_vm._v("Problemgeschichte")]), _vm._v(", a conception of the history of philosophy that is centered around the self-unfolding of perennial philosophical problems over time."), _c('a', {
    attrs: {
      "id": "rch1en17"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en17",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("17")])]), _vm._v(" This approach allows one to treat philosophy as an historical process, yet to also join various insights together across time into a broader corpus possessing scientific objectivity. This presupposes, of course, some access to the perennial problems. Hartmann himself acknowledges this: “we must already possess the problems in order to conduct a history of problems.”"), _c('a', {
    attrs: {
      "id": "rch1en18"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en18",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("18")])]), _vm._v(" To explain this possession and the conditions making this approach possible, Hartmann cites the capacity – indeed the necessity – of abstracting both from the concrete factual basis of philosophical insights and from the specific lives of individual thinkers. What remains are “problem-lines” of which particular people and eras are merely intersections."), _c('a', {
    attrs: {
      "id": "rch1en19"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en19",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("19")])]), _vm._v(" The problems that "), _c('em', [_vm._v("Problemgeschichte")]), _vm._v(" treats thus function as formal-transcendental categories that allow one to establish “historical continuity,” and any variability in the philosophical content thus attaches not to the problem, but merely to its historical instantiation."), _c('a', {
    attrs: {
      "id": "rch1en20"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en20",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("20")])]), _vm._v(" From this angle, then, the history of philosophy is a gathering process that collects knowledge from across history in a precise and cumulative procedure.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_17"
    }
  }), _vm._v("The philosophical import of history, then, was that humans sought to answer perennial questions, and past attempts to address these could be aligned with, and serve as material for, present research. One had to admit, of course, that questions undergo variations in their formulation, and even that present concerns were not always in direct correlation to those of the past. Yet the thinker of problematic history, so the Neo-Kantians imagined, could surmount these obstacles by normalizing the terms and conceptual structure under which past questions were raised – in short, by producing a transcendental logic of history. In this way, one could identify the transhistorical topics (or “values,” to use Rickert’s term"), _c('a', {
    attrs: {
      "id": "rch1en21"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en21",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("21")])]), _vm._v(") of historical reason, fusing them into one discourse. As a detached surveyor of this whole, the historical objectivist could take his or her own position as a sign of the scientific nature and scope of historical reflection.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("A divergent, discontinuous approach arose as a reaction to this unifying stance. This approach, while maintaining a quasi-scientific overview of history, stressed the limitations of comparing various times to each other. Rather than emphasizing the continuity between philosophical questions and concerns across time, one relativized the views of each age to its historical environment. This plurality and discontinuity demanded that historical research be mobile: that it place itself within the age under consideration and assess the latter’s concerns on their own terms.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Gadamer’s critique of these two approaches hinges on what unites them as historicist stances. First, both try to rise above history in such a way as to ignore the researcher’s embeddedness within it, and render his or her own standpoint insignificant. This naive leap constitutes a neglect of what Gadamer describes under the term of historical effect, and thus amounts to a misunderstanding of our historicity. The danger here, echoing Nietzsche’s second "), _c('em', [_vm._v("Untimely Meditation")]), _vm._v(", is that this account of history weakens one’s values, that in elevating ourselves we risk extinguishing ourselves and becoming mere capstones with no concerns or sources of our own from which to draw inspiration."), _c('a', {
    attrs: {
      "id": "rch1en22"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en22",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("22")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("As an initial step toward addressing this danger, Gadamer bluntly describes our situation as presenting limits that are finite and insurmountable: As historical beings, we belong to history more than it belongs to "), _c('a', {
    attrs: {
      "id": "page_18"
    }
  }), _vm._v("us."), _c('a', {
    attrs: {
      "id": "rch1en23"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en23",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("23")])]), _vm._v(" The present is characterized by its own questions and concerns, and any reflection on history, as for example the historical objectivist’s search for law-like patterns, is actually rooted in these current concerns. Thus, the problem of "), _c('em', [_vm._v("Problemgeschichte")]), _vm._v(" is that it fails to recognize the retrospective projection of its own peculiar questions as presently motivated concerns – not to mention its failure to capture the way in which genuine questions arise to begin with. In taking problems to remain the same across time, it imposes an artificial framework of sameness upon historical effect, normalizing language in the process and creating an insidious demand for identity – what amounts to a confirmation bias."), _c('a', {
    attrs: {
      "id": "rch1en24"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en24",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("24")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The relativist position fares no better under this charge, for even though it does not standardize the structure of its approach, it nevertheless believes that it can study past ages without having an intrinsic and generative standpoint of its own. Correlated to historicism’s detachment from history, then, is Gadamer’s second point of critique: the claim to immediacy. Once one has assumed an overview of history, one takes the objects within history to lie within immediate grasp. To cross from the present to the past and enter into the views of another age is, for the historical relativist, perhaps a difficult endeavor, but ultimately merely a matter of information and method. With the right approach, there is even the possibility of understanding past views better than they grasped themselves.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The historical objectivist espouses a similar dedication to method: he or she traces formal themes that purportedly remain the same across time, and leaps over great distances to connect them. One could view this, conversely, as the retrieval of past objects out of their original context into a present logical framework. Yet the logic of this framework makes the transmission of history appear to be immediate and automatic."), _c('a', {
    attrs: {
      "id": "rch1en25"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en25",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("25")])]), _vm._v(" A further step toward alleviating historicism’s danger, in Gadamer’s view, is therefore to recognize the mediating capacity of finitude: to begin from the actual differences between our situation and the one we intend to grasp. We cannot assume that past concerns were exactly the same as ours, even if similar terms are used in both instances. There is no immediate way to know, for example, whether the problem of freedom was of the same concern in antiquity as it is now; our emphasis on this problem could stem from our own current historical occupation with it."), _c('a', {
    attrs: {
      "id": "rch1en26"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en26",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("26")])]), _vm._v(" This "), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }), _vm._v("is not to say that questions across history cannot prove themselves to be similar and of comparable significance; however, the identity or difference of horizons – and thus of questions as well – emerges only "), _c('em', [_vm._v("in")]), _vm._v(" the encounter between horizons and their possible fusion. In other words, one must pose the questions as questions rather than prefigure them in advance. The identity of history cannot, as in "), _c('em', [_vm._v("Problemgeschichte")]), _vm._v("’s notion of continual problem-lines, be presupposed from the outset."), _c('a', {
    attrs: {
      "id": "rch1en27"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en27",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("27")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The first of Gadamer’s concerns with historicism can be resolved by formulating a philosophy that affirms human situatedness in history and that treats historical knowledge from within this situation. The observer of history, as Gadamer reminds us, is herself located in history. There still remains, however, the issue of immediacy, and it looms large even among positions that directly account for our historical situation. To fully resolve it, therefore, Gadamer must distance himself from two positions that seemingly lie on either side of his own philosophical stance, adumbrating it and rendering it indistinct.")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('em', [_vm._v("Immersive Historicism")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("On one side lies the position of traditionalism. Here, as with historical objectivism, the emphasis is on continuity over time. The traditionalist takes history to stay identical to itself, repeating itself forward without undergoing any substantive change. As there is no perspective upon history from the outside, as it were, history becomes just another name for the views that come to dominate, the canons that agglomerate, and the lineages that hold power. To practice historical reflection, then, is a matter of finding essential significance, of appropriating meanings to an account that remains univocal over time, or at least tends toward univocity. Moreover, as confined to and conditioned by this tendency, the historical subject cannot acquire any distance from its purported authority (nor, perhaps, even see the need to).")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Quite correctly, the traditionalist position has been criticized for its hegemonic-fatalist narrative and engulfing appropriation. Yet if, by contrast, one merely negates traditionalism and asserts a discontinuous history, one is left with a conception of various disjointed and incommensurable perspectives that echo the historical relativism treated "), _c('a', {
    attrs: {
      "id": "page_20"
    }
  }), _vm._v("above. With the added qualification that one is now situated within history, tied to one’s age and culture, this stance lacks the capacity to simply transfer out of one perspective and into another. One may recognize that there are many histories and ways of life, but the only standard of choice one knows is that by which one is already conditioned. This perspectivism, in turn, weakens the force of any claim one might make in one’s contemporary situation."), _c('a', {
    attrs: {
      "id": "rch1en28"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en28",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("28")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("To many readers of Gadamer, this latter perspectivism seems to ring close to his own position. Indeed, his denial of the existence of objective historical truth, coupled with the insistence on being situated within history, seems to place him squarely in this category by default. Yet there have also been those who take Gadamer to espouse a form of traditionalism, given his emphasis on our belonging to tradition."), _c('a', {
    attrs: {
      "id": "rch1en29"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en29",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("29")])]), _vm._v(" It is clear, however, that he cannot stand for both, and indeed he stands apart from either of them."), _c('a', {
    attrs: {
      "id": "rch1en30"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en30",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("30")])]), _vm._v(" Even to say that he falls somewhere in between might be incorrect. Gadamer’s avoidance of these stances rests upon his account of a mediated situation within history, but the success of this avoidance has not been widely recognized, for this requires a synthetic grasp of Gadamer’s position not just concerning historical interpretation, the history of ideas, or the philosophy of history, but regarding the historicity of human understanding as such."), _c('a', {
    attrs: {
      "id": "rch1en31"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en31",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("31")])]), _vm._v(" It is a universal and ontological project. After all, Gadamer does not simply reply with an alternative to theoretical historicism’s failed method or with a critical technique by which to emancipate oneself out of a given tradition or perspective."), _c('a', {
    attrs: {
      "id": "rch1en32"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en32",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("32")])]), _vm._v(" His aim is neither to perfect formal knowledge of history along Neo-Kantian lines nor to justify the accounts of a life completely enclosed by history (a justification that would remain in place if he simply sought a way to navigate out of a given situation of containment). Instead, he gives the ontological reasons why such perfection or enclosure is impossible to begin with, and it is to these reasons that I will turn in the next section.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In clarifying what Gadamer means, on the one hand, by our situated belonging to historical transmission and, on the other hand, by the mediating enactment of this transmission, I will show what kind of relation to the past he in fact espouses (a crossing-over of questions, a fusion within “one great horizon”"), _c('a', {
    attrs: {
      "id": "rch1en33"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en33",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("33")])]), _vm._v("), and what kind of truth he thinks can arise within human history (the productivity of distance and continued openness). "), _c('a', {
    attrs: {
      "id": "page_21"
    }
  }), _vm._v("Just as the positions elaborated above are not merely historical but also social-ethical stances, our clarification of Gadamer’s position will ultimately reveal his philosophy to be not just a critique of theories of history, but an elaboration of an ethical social discourse.")]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("BELONGING AND ENACTMENT")])]), _vm._v(" "), _c('h4', {
    staticClass: "h4a"
  }, [_c('em', [_vm._v("Situation")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Due to the opacity of the subject matter, the description of Gadamer’s concept of historical effect cannot proceed in a direct, definitional fashion. Our analysis will instead need to account for the many layers of human historicity, and also take great care in sorting out the terminology available to describe this phenomenon. To begin elucidating the situation in which we are said to belong to tradition, let us first address the elements of prejudice and horizon that fundamentally constitute this situation.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("What Gadamer means by prejudice is not the negative denotation of a fixed false judgment or belief, but instead the enabling fore-understanding that we have in our approach to anything. He underscores that “all understanding inevitably involves some prejudice,”"), _c('a', {
    attrs: {
      "id": "rch1en34"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en34",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("34")])]), _vm._v(" noting that we always peer out from our present situation with some concern, intention, impression, or judgment. Put simply, we do not gaze upon reality from a position of neutral observation. In this sense a prejudice is everything but false, in that it frames something precisely as we take it to be. Further, our perspective is not attached to any one position. Prejudice is not static and engrained as commonly conceived, but undergoes constant revision as new experiences and stances inform our views.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This may not yet be controversial in itself, for one could still retain a sense of one’s own masterful centrality amidst varying experiences and positions. If one had full control of one’s fore-understanding, one could determine which prejudices to admit and which to exclude. However, Gadamer stresses that the origin and arrangement of prejudices is not strictly autonomous or rational. Against the Enlightenment notion that we might free ourselves of all unwanted influences (“"), _c('em', [_vm._v("sapere aude!")]), _vm._v("”) and thereby make a clean incision between past and present, foreign and "), _c('a', {
    attrs: {
      "id": "page_22"
    }
  }), _vm._v("familiar, Gadamer contends that our conceptions, rational or otherwise, are not derived solely from our activity as subjects, and are thus not at our free disposal."), _c('a', {
    attrs: {
      "id": "rch1en35"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en35",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("35")])]), _vm._v(" Instead, they can be traced to our psychological experiences, our familial and social environment, our education and cultural tradition."), _c('a', {
    attrs: {
      "id": "rch1en36"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en36",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("36")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("We can never expect to begin an endeavor from scratch, as it were. Any behavior of our own takes place in a situation already configured by prior commitments, and even these are responses to factors beyond our control (and often beyond our awareness). Yet this should also not deceive us into thinking that prejudices stem entirely from external, causal forces. Indeed, prejudices are precisely not arbitrary and unidirectional influences. They are not forces at another’s whim that cover over or supplant one’s own agency, but are instead operative within all subjective acts, and constitute the very medium of our biography."), _c('a', {
    attrs: {
      "id": "rch1en37"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en37",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("37")])]), _vm._v(" Even if we are not aware of them as such, prejudices are the threads of our agency, woven together into an overall sense of reality that amounts to the scope and depth of one’s understanding. It is this fabric of prejudices that Gadamer has in mind when he speaks of one’s horizon.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("As woven together from prejudices, from the specific expectations of a given position, a horizon is by definition limited. However, given what we have already said about prejudices and the kind of subjectivity that goes along with them, this limitation must itself be characterized appropriately. Rather than being the limitations of a fixed entity existing in itself, horizons are limits that can move, expanding and contracting as positions shift and new understanding takes place. In this way, they are as ungrounded, flexible, and revisable as the prejudices that constitute them. Similarly, horizons are also not one’s own in the sense that one is in control of their scope; they arise in response to the world, and constitute only a provisional familiarity. One can certainly become more conscious of one’s horizon – a point I will develop in more detail below – but if one were to attempt an inventory of one’s understanding, one would find much that is foreign. Indeed, one can never trace out the perimeter of what is enclosed by one’s horizon, as its origin lies in a past beyond retrieval and in sources that are most certainly unfamiliar.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Yet as we saw in the case of prejudice, this strangeness is also not utterly foreign and imposed from outside. The incomplete familiarity of "), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }), _vm._v("one’s horizon, as well as the inability to delimit it, lead Gadamer to call this perimeter “open,” and this extends to any horizon by definition."), _c('a', {
    attrs: {
      "id": "rch1en38"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en38",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("38")])]), _vm._v(" If, accordingly, no horizon is bounded completely within and for itself, an encounter with something beyond our horizon is not an experience of utter alterity or strangeness. This other horizon does not exist across a chasm that must first be bridged through transposition into what is foreign (as in the case of reconstructionist worldview-philosophy) or appropriation into the sphere of sameness (as with theoretical historicism or traditionalism). Nor, along perspectivist-incommensurabilist lines, is the other horizon sealed off from any understanding whatsoever. Rather, there is always some connection of horizons through which we understand what is beyond our own, and Gadamer attributes this connection to “one great horizon.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("An ancient civilization may have engaged in practices that we consider revolting or immoral, and lived a daily existence that bears virtually no resemblance to our own. Yet there are aspects of life, perhaps not easily indicated, that this ancient culture shares with the present: concerns for shelter and survival, recognition of authority, and expression of meaning. Even more fundamentally, there is the basic historicity and linguisticality of these common concerns: they once took place, and continue to reverberate, in human time and language. The life of this culture need not have influenced the present in any direct fashion for these shared aspects to arise. The elements of shared life stem, rather, from a horizon broader than either the past or the present."), _c('a', {
    attrs: {
      "id": "rch1en39"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en39",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("39")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This broad horizon is, then, properly conceived as a condition (or medium) of all understanding."), _c('a', {
    attrs: {
      "id": "rch1en40"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en40",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("40")])]), _vm._v(" Any partial horizons that come into contact with one another are ultimately connected by the “higher universality” that pervades them both."), _c('a', {
    attrs: {
      "id": "rch1en41"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en41",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("41")])]), _vm._v(" After all, if every horizon is fundamentally open and ungrounded, it does not stand on its own apart from history, but is a part of this larger whole. Thus, when Gadamer speaks of the “fusion” of horizons, it is not the meeting and amalgamation of two separate and preexisting entities, a confluence that would assimilate one side to the sameness of the other. Instead, “understanding is always the fusion of the horizons "), _c('em', [_vm._v("supposedly")]), _vm._v(" existing by themselves,”"), _c('a', {
    attrs: {
      "id": "rch1en42"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en42",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("42")])]), _vm._v(" and in fact both the difference between the horizons and their unity emerge only in the understanding encounter itself. This is not to diminish difference, "), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }), _vm._v("but to emphasize that it is something that occurs, and that takes shape differently in each occurrence. What enables these events of difference is the overall horizon of history. It is crucial to note once more, however, that this is the lived, experienced history of shared human life, and not the formal-transcendental history of Neo-Kantian "), _c('em', [_vm._v("Problemgeschichte")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Our analysis remains imprecise, however, if we simply account for the movement of difference, the event-character of understanding, on the subjective side of historicity, as if it occurred against a permanent backdrop or from out of a stable unity. Even if it is clear that we are dealing with history as an ontological experience rather than a transcendental category, we must avoid reducing self and other to mere epiphenomena of one underlying and fixed totality. To achieve this, one has to pay close attention to Gadamer’s description of the whole of history. On the one hand, it is not a structural or formal condition for the occurrence of understanding. The oneness of horizons means more than the simple temporal provision of possibility for encounter. Accordingly, history is more than a container – real or imagined – for shared life; it actively contributes to the emergence of prejudice and the formation of horizons, and does so not just once, but constantly. As I will elaborate shortly, this has decisive consequences for the conception of history’s effect.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On the other hand, when Gadamer develops his concept of the oneness of horizon(s), he takes caution not to define its content too precisely. To be sure, this lack of articulation leaves much room for interpretation, and has led to many a misunderstanding of Gadamer’s position. Yet this vagueness is precisely intended to eschew any association with a traditionalism that would imbue shared life with a specific content. The ambiguity also helps Gadamer in avoiding such pitfalls as the Kantian notion of an infinite task of reason, the Neo-Kantian formulation of permanent problems, the Hegelian teleology that grasps history absolutely, and the Heideggerian uncovering of a fundamental question that subsumes all subjectivity. Against these conceptions, Gadamer asserts the unity of history to consist not in any definite progression, but in an open, fluid movement that articulates itself “from within.”"), _c('a', {
    attrs: {
      "id": "rch1en43"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en43",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("43")])]), _vm._v(" The one horizon of tradition ultimately has as little fixity as the horizons it gives rise to. Just as no partial horizon within history is complete, so the horizon of time itself is still open and unfolding."), _c('a', {
    attrs: {
      "id": "rch1en44"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en44",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("44")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _vm._v("It should therefore be clear why, given the nature of our historical situatedness, we must revise prior conceptions of historicality, including their assumptions of immediacy. We neither stand detached from nor are we completely subsumed by our historical situation, so we are never in a state of pure agency or pure submission to external factors. In fact, we are identical to the situation itself, inasmuch as our understanding at any point is partial, but not constrained to fixed limits. Our position is constantly generated anew in the occurrence of encounter, and this event is itself part of a movement unlike any linear causality that would have a traceable beginning. The situation, then, “is an event and not a process.”"), _c('a', {
    attrs: {
      "id": "rch1en45"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en45",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("45")])]), _vm._v(" The overall horizon of tradition in which we are situated does not possess the stability of a ground, only the unity of (a) movement. Gadamer refers to this movement as the occurrence of transmission.")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('em', [_vm._v("Transmission")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("The analyses of prejudice and horizon have shown that we are situated in a structure that exceeds our individual rational powers. We are both thrown into historicity and carried along by it in such a way that our doing is never entirely our own; it belongs to the greater horizon of the movement of tradition. Yet while I have emphasized that this belonging does not amount to an external determination of our agency, this assertion remains to be substantiated through Gadamer’s own concepts; it remains to be elaborated what exactly history’s effect amounts to. Whereas we have seen that we do not occur without history, it bears exhibiting how history also does not occur without us.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("We need not look far for a sense of this reversal. The term Gadamer selects for the transmission of tradition, "), _c('em', [_vm._v("Überlieferung")]), _vm._v(", denotes a delivering-over from the past to the present and concisely indicates an intertwined connection. Gadamer has already dismissed the notion of our autonomy over history, so the subject cannot serve as the sole agent of historical transmission. In ruling out, further, that this process could occur automatically, Gadamer avoids the lawful naturalism of theoretical historicism and the assimilative grasp of traditionalism. Finally, and perhaps most crucially for his determination of historical effect, he also denies that the agency of transmission lies in history itself as if in some "), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }), _vm._v("higher force, ruling out eschatological-teleological explanation and the notion of a quasi-Heideggerian “destiny of Being” ("), _c('em', [_vm._v("Seinsgeschick")]), _vm._v("). This leaves a chiasmic structure of reciprocal effect as the only alternative."), _c('a', {
    attrs: {
      "id": "rch1en46"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en46",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("46")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("As “the reality of history within understanding,”"), _c('a', {
    attrs: {
      "id": "rch1en47"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en47",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("47")])]), _vm._v(" transmission indeed brings about one’s stance in history, but not as a metaphysical or naturalistic "), _c('em', [_vm._v("archê")]), _vm._v(". The causality involved is instead one of ongoing effect ("), _c('em', [_vm._v("Wirkung")]), _vm._v(")."), _c('a', {
    attrs: {
      "id": "rch1en48"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en48",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("48")])]), _vm._v(" Gadamer found inspiration for this historical causality in Aristotle’s conception of "), _c('em', [_vm._v("energeia")]), _vm._v(" as a being-at-work, and the latter also informs the concept of “play” in Gadamer’s aesthetic analyses."), _c('a', {
    attrs: {
      "id": "rch1en49"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en49",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("49")])]), _vm._v(" This constant being-in-effect intertwines both past and present, the effecting and the affected. Gadamer speaks of an interplay between “the movement of tradition and the movement of the interpreter,”"), _c('a', {
    attrs: {
      "id": "rch1en50"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en50",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("50")])]), _vm._v(" which, given the analysis above, amounts to the fusion of the individual horizon with the horizon of historical transmission itself. Yet this interplay must be something other than an exchange between two distinct entities."), _c('a', {
    attrs: {
      "id": "rch1en51"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en51",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("51")])]), _vm._v(" We have already seen that the split between purportedly stable subjects and objects is senseless. Not only are the two involved dialectically – the past shapes my horizon just as I recognize the past and understand it anew – but this involvement takes place on an ontological rather than methodical level. The involvement is itself the “one great horizon” of an ongoing movement. Our belonging to historical effect is thus not so much a situation we are in, an a priori condition we are exposed to, as much as it is a process that we are and enact. If tradition is an ongoing event, and understanding a crucial perpetuation of this, then “understanding itself is a historical event.”"), _c('a', {
    attrs: {
      "id": "rch1en52"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en52",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("52")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This bespeaks far more generative participation than the term “‘belonging” would initially imply. We may belong to historical transmission more than we possess ourselves, but tradition also belongs to us more than it rests on its own apart from us. If tradition is not a brute force like nature that operates without our action, we must somehow be involved in its continuation."), _c('a', {
    attrs: {
      "id": "rch1en53"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en53",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("53")])]), _vm._v(" Indeed, what arises in history never does so without the mediation of those who take part in it. Tradition, the transmission of history, is actually more aptly conceived as the mediation itself, as opposed to the sedimented past that is merely carried forward (by either its own or some other’s impetus)."), _c('a', {
    attrs: {
      "id": "rch1en54"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en54",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("54")])]), _vm._v(" In this way it is far clearer how we ourselves play a role in perpetuating and preserving tradition. Our "), _c('a', {
    attrs: {
      "id": "page_27"
    }
  }), _vm._v("historicity ("), _c('em', [_vm._v("Geschichtlichkeit")]), _vm._v(") is not simply conditioned by a permanent effect ("), _c('em', [_vm._v("Wirkung")]), _vm._v("), but itself bears upon transmission as a historicity of effect ("), _c('em', [_vm._v("Wirkungs-Geschichtlichkeit")]), _vm._v("). In the reciprocity that thus obtains between past and present, there is a “unity of effect”:"), _c('a', {
    attrs: {
      "id": "rch1en55"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en55",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("55")])]), _vm._v(" To speak of tradition is to announce the continual preservation of the past. Put differently, the enactment of history’s effect constitutes our very being.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("To exhibit this more clearly, one need only think of how tradition is in fact delivered over to and through the present. Without human effort, no idea, object, or event retains its significance, and by embracing some things rather than others, we alter the substance of history. Thus, everything we find with us today has in some way been preserved through an act of mediation, and it needs this mediation if it is to perdure. Yet this ongoing transmission need not occur explicitly as a conscious, subjective act. Gadamer underscores that “preservation is an act of reason, though an inconspicuous one.”"), _c('a', {
    attrs: {
      "id": "rch1en56"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en56",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("56")])]), _vm._v(" It already occurs when we take over the historical horizon we are given, and continues in our everyday social concerns and aspirations. However future-oriented we may be, and however vehemently we might rebel against what preceded us, our participation in transmission will always persist. As an example, Gadamer takes what we consider the “classical” to embody this mode of historical existence that continues through our involvement, but not necessarily on the basis of a conscious decision, and certainly not due to preformulated standards. It persists, rather, on the basis of its ability to speak to us and to compel our continued affirmation."), _c('a', {
    attrs: {
      "id": "rch1en57"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en57",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("57")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Through this exemplar, we can fully uncover and summarize the intertwined structure of transmission. In one respect, tradition cannot persist without the engagement of humans who value and cultivate some things over others; without our concerns and commitments, enduring preservation would cease. Our attempts to understand the world inherently draw on and converse with, but also revise, transform, and subvert the precedents and prejudices we inherit. In another respect, however, there are limits to this transformation. First, it is impossible to start completely anew, to eradicate all connection to the past. As always engaged in the transmission of the past, we perform its preservation whether we actively choose to do so or not. Second, it is equally impossible to ever complete this mediation, to establish once and for all what is worth "), _c('a', {
    attrs: {
      "id": "page_28"
    }
  }), _vm._v("preserving, or to know definitively what can be brought forth to endure from history. We can never fully know history, nor our place in it.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Since history is itself an incomplete whole, a moving horizon and not a totality, it retreats from our final grasp at the same time as it lies open to our ongoing engagement and interpretation from within. Tradition both gives itself over to our understanding and withdraws from it. This ambiguous nature of tradition, for Gadamer, contributes to the seemingly paradoxical character of our finite historicity: Our existence is an infinite possibility of meaning within a bounded situatedness of limited understanding."), _c('a', {
    attrs: {
      "id": "rch1en58"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en58",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("58")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Yet while we tend to have a blind spot for our own historicity, this finitude need not remain inconspicuous."), _c('a', {
    attrs: {
      "id": "rch1en59"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en59",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("59")])]), _vm._v(" One might wonder, in fact, whether a form of awareness could alleviate the typical opacity of our involvement in transmission. Gadamer’s own analyses appear to answer this in the affirmative. After all, his philosophical hermeneutics serve as a higher reflection on our finitude, as a conscious and theoretical description of what occurs in all pretheoretical situatedness. In this respect, we seem to be capable of rising above our finitude, but it remains to be seen how far this elevation takes us.")]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("REFLECTION, FINITUDE, AND OPENNESS")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("When Gadamer introduces the notion of a consciousness of historical effect ("), _c('em', [_vm._v("wirkungsgeschichtliches Bewusstsein")]), _vm._v("), one might be tempted to view this as a step beyond the intertwined nature of transmission to a rational subsumption that enables the proper adjudication of prejudices. The desire for this movement is, philosophically and hermeneutically speaking, quite understandable. Indeed, were Gadamer to assert the opposite, that no awareness of our finitude is possible, he would succumb to the accusation of incommensurable perspectivism, in which a specific cultural tradition absolutely, prerationally, and irrevocably determines our consciousness.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Contrary to both of these stances, however, the consciousness introduced here amounts neither to a reflective critical distance nor to a realization of perspectival determinism. Rather, this additional step on Gadamer’s part serves only to extend the ambiguity and paradoxical "), _c('a', {
    attrs: {
      "id": "page_29"
    }
  }), _vm._v("finitude already at work in historical effect itself. If historical effect encapsulates both our belonging to tradition and our enactment of it, then the consciousness of this situation, by nature of its double genitive, entails another layering "), _c('em', [_vm._v("within")]), _vm._v(" historical effect rather than beyond it. This is evident in Gadamer’s term itself, which is frequently also rendered in English as “historically effected consciousness.” As a consciousness “of” historical effect, “it is used to mean at once the consciousness effected in the course of history and determined by history, and the very consciousness of being thus effected and determined.”"), _c('a', {
    attrs: {
      "id": "rch1en60"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en60",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("60")])]), _vm._v(" This extension of the chiasmic structure of historical effect, while ultimately quite revealing of Gadamer’s theory as a whole, is an element that has evoked many misunderstandings of his position. By elaborating in more detail how consciousness relates to historical effect, I aim to correct these errors while accounting for the immense importance that Gadamer attributes to the consciousness of historical effect.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("One might be tempted to conceive of this consciousness as “that which is involved in historical effect.” Regardless of this involvement’s character of occurrence, then, from this point of view it remains an object of detached interest, or that which was analyzed under the heading of historicity and finitude in the previous section. The term “consciousness,” then, merely refers to the situated character of any human’s existence whatsoever. This third-person perspective, one might say, does not rob the particular consciousness of its individuality; the fact that consciousness has a blind spot for its situatedness, that the latter always retreats from view, does not diminish the specifically conscious nature of historical life, which philosophy in turn can investigate. As already indicated, one need not be aware of historical transmission in order to take part in it, and just because this transmission takes place inconspicuously does not amount to any sort of unconsciousness on the part of the historical human.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("If, however, the consciousness that is typically involved inconspicuously in historical effect has become an object of hermeneutical investigation, then the results of this analysis constitute another, less detached meaning of the consciousness of historical effect. In conducting its analyses, our consciousness – which is no less historical than the kind being analyzed – has indeed shown that “it can rise above that of which "), _c('a', {
    attrs: {
      "id": "page_30"
    }
  }), _vm._v("it is conscious,” the latter being the hermeneutic situation as described earlier."), _c('a', {
    attrs: {
      "id": "rch1en61"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en61",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("61")])]), _vm._v(" Yet since we are that very situation, this consciousness of historical effect amounts to a kind of self-reflection, and the preposition “above” loses its seemingly detached objectivity.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Given the claims regarding historicity and finitude that are made at this level of reflection, more needs to be said concerning the status of this purportedly elevated consciousness. Gadamer, in fact, denies that this higher-order consciousness can reach a level of atemporal universality, that it can regard history and human existence in a logically scientific manner."), _c('a', {
    attrs: {
      "id": "rch1en62"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en62",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("62")])]), _vm._v(" Instead he insists that, like situated historicity, even higher-level consciousness “is so radically finite that our whole being, effected in the totality of our destiny, inevitably transcends its knowledge of itself.”"), _c('a', {
    attrs: {
      "id": "rch1en63"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en63",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("63")])]), _vm._v(" This, Gadamer contends, is a “fundamental insight which is not to be limited to any specific historical situation.”"), _c('a', {
    attrs: {
      "id": "rch1en64"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en64",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("64")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("With this, Gadamer underscores that all reflection upon history, regardless of the elevation it indeed attains, still belongs to the effect of transmission. If the consciousness of this historical effect cannot subsume historicity, then, but instead remains under the universal sway of it, then consciousness cannot detach itself from tradition, nor can it seek unchanging knowledge of historical laws. It cannot make metaphysical claims concerning history’s substance or "), _c('em', [_vm._v("telos")]), _vm._v(", nor can it become the sort of critical reflection typically pursued since the nineteenth century’s discovery of historical consciousness. In short, it does not possess the status or aims of a technical skill, an a priori account of values, or a natural science. This, for Gadamer, actually liberates the concept of reflection from the narrow confines of methodical and teleological procedure, from abstruse academic debates and their concomitant inscrutable systems. Instead, it underlines that the consciousness of historical effect remains an inherent and constant possibility for any participant in history. Such reflection is not confined to the philosopher’s theoretical analysis but pervades all interpretive tasks of social life and human conduct."), _c('a', {
    attrs: {
      "id": "rch1en65"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en65",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("65")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Yet this is not to flatten historical reflection to the point that it becomes indistinct from any other human action. The relinquishment of technical prowess and scientific method is not to deny that historically effected consciousness pursues its reflection “in a regulated way,”"), _c('a', {
    attrs: {
      "id": "rch1en66"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en66",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("66")])]), _vm._v(" nor that there are criteria for its successful operation. Indeed, by showing "), _c('a', {
    attrs: {
      "id": "page_31"
    }
  }), _vm._v("that the consciousness of historical effect “is at work in all hermeneutical activity,”"), _c('a', {
    attrs: {
      "id": "rch1en67"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en67",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("67")])]), _vm._v(" Gadamer can trace historical reflection to the element of application that is intrinsic to such activity, and by elaborating on what such application entails, also elucidate reflection itself. This avoids the pernicious adversary to scientism in nineteenth- and early-twentieth-century debates on historicism, the relativistic agglomeration of "), _c('em', [_vm._v("Weltanschauungen")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Some of Gadamer’s most popular and poignant examples of hermeneutical activity include the interpretations performed by judges, theologians, literary critics, and historians. While varying in their degree of technicality and precision, these interpretations share in common the employment of historical understanding in the grasping of their objects. That is to say, the general object under consideration is never a delimited universal in itself that is subsequently applied, ready-made, to a current situation. Nor, conversely, is that object subsumed as a particular under universal laws of judgment. Instead, the general object – be it law, sacred text, literary object, or historical event – is first understood in and through the conscious formulation of its relevance to the present. The changing demands of the present thus affect the understanding and application of the object. The demanding nature of application therefore lies in the fact that it never consists in mere adherence to fixed standards or rules of interpretation, as if one’s situation (or that which is applied to it) were a stable entity. Since “the movement of reflection has its place within the context of a beginningless and endless tradition,” there is no such stability."), _c('a', {
    attrs: {
      "id": "rch1en68"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en68",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("68")])]), _vm._v(" Rather, given the fluid mobility of our horizons and of transmission as such, application remains an ongoing activity.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Gadamer, once again drawing on Aristotle, finds a further exemplar and conceptual analogue for the ongoing nature of application in the concept of practical wisdom or "), _c('em', [_vm._v("phronêsis")]), _vm._v(". This central insight of the "), _c('em', [_vm._v("Nicomachean Ethics")]), _vm._v(" reveals that ethical action involves a continual exchange between one’s moral ideals and the given situation one finds oneself in. It requires having an attentive openness to the demands of one’s circumstances, while at the same time considering what response to the situation best preserves one’s broader commitments."), _c('a', {
    attrs: {
      "id": "rch1en69"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en69",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("69")])]), _vm._v(" Transposing this ethical structure into the hermeneutical,"), _c('a', {
    attrs: {
      "id": "rch1en70"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en70",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("70")])]), _vm._v(" Gadamer thereby underscores that “the relation between the universal and the particular "), _c('a', {
    attrs: {
      "id": "page_32"
    }
  }), _vm._v("is not that of logical subsumption, but rather of co-determination.”"), _c('a', {
    attrs: {
      "id": "rch1en71"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en71",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("71")])]), _vm._v(" Taking this circular exchange to underlie all interpretive engagement between selfhood and alterity, Gadamer can clarify the task of application – and thus of historical reflection – as an openness to ongoing, mutual determination.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Yet just as one might ask how the "), _c('em', [_vm._v("phronimos")]), _vm._v(" enters into the stance of circumspective openness, one can wonder how the consciousness of historical effect comes to acknowledge the need for, and engage in the practice of, continual historical application. On the one hand, this question could be taken to misunderstand the character of historical reflection, as if the latter were a resolute decision on the part of the subject. A far better conception would be one that takes application to lie among the individual’s constant interpretive possibilities. Yet the question of the genesis of application, on the other hand, pertains to the real aspect of application as a task. After all, even though it is always a possibility for us, this reflective exchange arises only if application becomes actualized or necessary for one reason or another. Just as a student of Aristotle’s will concern himself only with the ethical potential of his situation if his upbringing has taught him to attend to it – that is, if he is experienced or self-aware enough to apply ethical knowledge"), _c('a', {
    attrs: {
      "id": "rch1en72"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en72",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("72")])]), _vm._v(" – so our (reflective) concern with our finite situation will arise only out of some form of experience.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("To become conscious of historical effect, historicity thus apparently needs teachers of its own, or at least an experiential path to reflection. Yet what sort of experience could generate this movement? Gadamer has already ruled out an internal origin, in which application would stem merely from the subject’s own resolve to reflect upon her prejudices. Such a conception of experience is entirely too voluntaristic. Our prejudices may be our own, and weave together to constitute our reality, but they belong to a movement that is not entirely within our control, and become visible to us only through engagement with something that provokes them."), _c('a', {
    attrs: {
      "id": "rch1en73"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en73",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("73")])]), _vm._v(" It bears noting, however, that application does not simply arise on the basis of external influence, either. It is only by putting our prejudices at risk that we experience – and potentially broaden – our horizon. Put differently, it is the experience of negativity, or “genuine experience,” that “teaches us to acknowledge the real.”"), _c('a', {
    attrs: {
      "id": "rch1en74"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en74",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("74")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_33"
    }
  }), _vm._v("Entering into the activity of application, then, involves the same experience as that of continually engaging in application itself: putting one’s position at risk over against something foreign, and allowing each to codetermine the other. The reflection involved in the consciousness of historical effect, therefore, entails hearing “tradition’s claim to validity, not in the sense of simply acknowledging the past in its otherness, but in such a way that it has something to say to me.”"), _c('a', {
    attrs: {
      "id": "rch1en75"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en75",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("75")])]), _vm._v(" Crucially for Gadamer, and for everything that follows in our analysis here, this means that the oneness of horizons described above is more than a mere background condition of experience; it is also an achievement on the part of the individual."), _c('a', {
    attrs: {
      "id": "rch1en76"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en76",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("76")])]), _vm._v(" The experience that brings forth reflection, while not strictly subjective, is still connected to an individual’s activity, and this activity permits of gradations; it can be performed well or poorly. It is by this evaluative standard of engagement that the application of historical reflection can be assessed.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Here, Gadamer’s notion of truth apart from method becomes more apparent. As “a genuine form of experience ["), _c('em', [_vm._v("Erfahrung")]), _vm._v("],” historically effected consciousness shares the structure of experience as such, and Gadamer conceives of both in decided opposition to the scientific notions of induction, repeatability, and positivity."), _c('a', {
    attrs: {
      "id": "rch1en77"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en77",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("77")])]), _vm._v(" Rather than seeking the closure of established fact and treating experience “in terms of its result,” Gadamer contends that genuine experience “is a process” and knows itself to be contingent."), _c('a', {
    attrs: {
      "id": "rch1en78"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en78",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("78")])]), _vm._v(" Similarly to the way in which the reflective aspect of historically effected consciousness exceeds “the limitations of reflective philosophy”"), _c('a', {
    attrs: {
      "id": "rch1en79"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en79",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("79")])]), _vm._v(" and has no beginning or end, experience has a completely different sense of completion ("), _c('em', [_vm._v("Vollendung")]), _vm._v(") here than it does in scientific methodology."), _c('a', {
    attrs: {
      "id": "rch1en80"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en80",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("80")])]), _vm._v(" Drawing on the notion of negativity in Hegel to develop this conception of experience (a concept that Gadamer takes to be “one of the most obscure we have”"), _c('a', {
    attrs: {
      "id": "rch1en81"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en81",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("81")])]), _vm._v("), Gadamer concludes that “[t]he dialectic of experience has its proper fulfillment not in definitive knowledge but in the openness to experience that is made possible by experience itself.”"), _c('a', {
    attrs: {
      "id": "rch1en82"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en82",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("82")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("As the result of a dialectic, this openness does not stem from a decision on the subject’s part, nor does it constitute a relinquishment of subjectivity in the face of overwhelming experience; it is not an accession to foreign impositions, as some have misunderstood it to be. Instead, this openness can be viewed as an extension of, and now a conscious "), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }), _vm._v("engagement with, the ontological chiasm that was shown to reside at the basis of historicity. There, at the root of our familiarity with the world, lay a fundamental openness of our individual horizon onto the broader horizon of transmission itself. The two could ultimately not be delimited as subject and object. Now, at the level of consciousness, the same intertwining persists. Even when we retrieve our horizon into reflection, we can never fully envelop our effectedness. The reversibility between self and tradition is thus not a closed circle, but remains open."), _c('a', {
    attrs: {
      "id": "rch1en83"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en83",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("83")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This constitutes Gadamer’s fundamental insight concerning the pervasiveness of finitude, and it has significant repercussions for any conception of historical reflection. The fact that the consciousness of historical effect repeats the chiasmic structure of that effect means that they are not two distinct operations. The conscious reflection merely echoes the historical structure of our existence; it is an experience of experience, an understanding of understanding, and to this extent can be called reflective. Yet since we cannot reflect ourselves out of our situation, this reflection does not stand above history or historical life; neither is it confined, however, within the prejudices of one’s time and place, or within a univocal boundary of a specific tradition. To be sure, the openness of historically effected consciousness amounts to a form of humility before the ongoing movement of tradition, but this tradition is not the transmitted endowment of a given culture, nor is it fixed to a given meaning. The meaning of history is, rather, infinitely revisable, and this revision is a continual task. However, such a task cannot become just another Rickertian transcendental value; it arises only once the openness toward historicity has become necessary, and this depends on the experience of negativity or ruptures. To underscore an earlier point, history cannot be presumed in advance to be “about” any particular meaning or project; its questionability precludes any formulation of perennial problems. Accordingly, historically effected consciousness is far more a decentered stance or attitude of lived questionability than a critical-logical thought process; it is “more ‘being’ ["), _c('em', [_vm._v("Sein")]), _vm._v("] than consciousness ["), _c('em', [_vm._v("Bewusstsein")]), _vm._v("].”"), _c('a', {
    attrs: {
      "id": "rch1en84"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en84",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("84")])])]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("CONSCIOUSNESS OF HISTORICAL EFFECT")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Despite the limitations that Gadamer extends to the consciousness of historical effect, he nevertheless takes this consciousness to reside in a "), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }), _vm._v("crucial position apart from traditionalism or perspectivism. Gadamer takes the significance of this position – indeed, the dire need for it – to lie in several connected hermeneutic aspects, namely the cognitive, reproductive, and normative. Emilio Betti had initially distinguished these categories in his "), _c('em', [_vm._v("General Theory of Interpretation")]), _vm._v(", but Gadamer takes issue with this distinction and asserts that “all three constitute one unitary phenomenon.”"), _c('a', {
    attrs: {
      "id": "rch1en85"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en85",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("85")])]), _vm._v(" In the following pages, I will draw out these aspects and demonstrate their unity, thereby arriving at one of the central premises of the present work, namely that the consciousness of historical effect is significant not only for the narrowly theoretical and interpretive application of historical reflection, but – as a universal characteristic of understanding – for social existence as such.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On the opening page of "), _c('em', [_vm._v("Truth and Method")]), _vm._v(", Gadamer asks “what kind of knowledge and what kind of truth” arise in the understanding of tradition."), _c('a', {
    attrs: {
      "id": "rch1en86"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en86",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("86")])]), _vm._v(" As we have seen, he takes previous accounts of historical knowledge to be inadequate, and presents his own account of historically effected consciousness as a response to this question. The answer boils down to the claim that historically effected consciousness contributes to scientific knowledge. In a passage that encapsulates many of his core assertions, Gadamer notes that “[o]ur need to become conscious of effective history is urgent because it is necessary for scientific consciousness ["), _c('em', [_vm._v("wissenschaftliche Bewusstsein")]), _vm._v("].”"), _c('a', {
    attrs: {
      "id": "rch1en87"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en87",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("87")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Of course, it should be clear from our previous analyses that Gadamer does not have the narrow, methodical standpoint of natural science in mind here, but instead the broad scope of any inquiry into truth. In stating the necessity of reflection so strongly, he is underlining the universal claim of his insight into historicity. After all, the “genuine” or dialectical experience that he proposed as underlying historical reflection should not be viewed as a devolved form of rigorous induction, as something inadequate to the high standards of science and relegated to the arts as the antechamber of science. Instead, dialectical experience constitutes the very origin of any insight and principle whatsoever."), _c('a', {
    attrs: {
      "id": "rch1en88"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en88",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("88")])]), _vm._v(" Furthermore, just as Gadamer shows this origin to lie in a movement of negativity, so too the fulfillment of experience consists only in openness to further experience. Thus, the whole conception of science here is one of ongoing investigation and revision, of movement within an indeterminate whole. Given the limits that Gadamer has indicated, a historically "), _c('a', {
    attrs: {
      "id": "page_36"
    }
  }), _vm._v("reflective scientific consciousness would therefore proceed along the same path as before, but with the recognition that any absolute fulfillment is precluded."), _c('a', {
    attrs: {
      "id": "rch1en89"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en89",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("89")])]), _vm._v(" Gadamer’s assertion of finitude, then, amounts not to a denial of progress, but to a reconception of it."), _c('a', {
    attrs: {
      "id": "rch1en90"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en90",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("90")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Gadamer is aware that his reconceptions in general, and this one in particular, ask “something unusual of the self-understanding of modern science.”"), _c('a', {
    attrs: {
      "id": "rch1en91"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en91",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("91")])]), _vm._v(" Indeed, he asks nothing less than an overhaul of this very self-understanding. The intensity and breadth of Gadamer’s demand derives from the fact that our entire engagement with the world tends to be conceived from the side of modern science. The urgency “that we should understand ourselves better” thus arises from the “actual deformation of knowledge” that can occur through this skewed scientistic emphasis."), _c('a', {
    attrs: {
      "id": "rch1en92"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en92",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("92")])]), _vm._v(" The cognitive significance of historically effected consciousness, then, lies in its delimitation of what human knowledge is and can be, and in its discovery of a truth beyond method. This consciousness therefore figures equally as importantly in the self-understanding of science as it does in human self-understanding in general.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This self-understanding, of course, cannot be equated here with self-consciousness, with absolute self-reflection, or with any rational interiority of modern proportions."), _c('a', {
    attrs: {
      "id": "rch1en93"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en93",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("93")])]), _vm._v(" This is because, as we have seen, no subject exists in complete isolation from its other, so that “[u]nderstanding is understanding of the self and the other "), _c('em', [_vm._v("at the same time")]), _vm._v(".”"), _c('a', {
    attrs: {
      "id": "rch1en94"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en94",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("94")])]), _vm._v(" Further, it is not simply subjects who advance science, but historically transmitted change itself."), _c('a', {
    attrs: {
      "id": "rch1en95"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en95",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("95")])]), _vm._v(" Thus, the sort of self-understanding that historically effected consciousness grants is an insight into the shifting nature of one’s horizon and the movement of tradition. It is the point at which understanding “knows itself” as an effect."), _c('a', {
    attrs: {
      "id": "rch1en96"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en96",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("96")])]), _vm._v(" Yet this sort of adequate understanding of one’s situation, and indeed any reflective engagement with alterity, can occur only if one takes account of one’s prejudices."), _c('a', {
    attrs: {
      "id": "rch1en97"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en97",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("97")])]), _vm._v(" I have already indicated that, since so much of one’s horizon is shaped historically (one need only think of tacit cultural and metaphysical assumptions), the awareness of these prejudices can arise only when they are challenged. A true understanding of oneself depends on the experience of negativity, and this includes acknowledgment of something beyond oneself. Ultimately, then, the recognition of the revisability of knowledge depends on a fundamental openness.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_37"
    }
  }), _vm._v("This openness and the decentering, dialectical experience that undergirds it contribute to what I have called the reproductive and normative significance of the consciousness of historical effect. The reproductive, or as Gadamer would rephrase it, productive aspect of this consciousness is that it recognizes both the forward movement in all understanding and the contribution that distance makes to this progress."), _c('a', {
    attrs: {
      "id": "rch1en98"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en98",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("98")])]), _vm._v(" Taking the nature of transmission to heart, one knows that no outright retrieval of the past is possible: first, it does not exist as a distinct and self-enclosed entity, but belongs to a movement of which we are a part as well; second, any understanding of the past is a conjunction or fusion of this past with one’s lived present, and will always carry with it the task and residue of application. This has the scientific consequence that a detached reconstruction like the history of problematics becomes impossible."), _c('a', {
    attrs: {
      "id": "rch1en99"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en99",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("99")])]), _vm._v(" Yet it also carries with it an implication for any involvement with history, namely that the past cannot simply be appropriated into complete familiarity or kept in place as dogmatic authority. It must be reproduced through a continued conversation.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In this necessity lies the normative character of historically effected consciousness. As Gadamer underscored with his assertion of the unity of this consciousness, the normative dimension is not a notion appended to the hermeneutical situation, as if it were the philosopher’s demand of what reflection should someday become. Rather, Gadamer’s aim is to account for what is given in the structure of human existence itself, and he draws his conclusions strictly from these conditions."), _c('a', {
    attrs: {
      "id": "rch1en100"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en100",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("100")])]), _vm._v(" In this way, the task of historical reflection that I described in the previous section is not a distinct stage added to the structure of historical consciousness, but arises from an adequate understanding of this very consciousness. Our historicity already involves an inconspicuous dialogue with the past, and historical reflection merely raises the ontological factors of this dialogue to awareness. Precisely because this awareness cannot reach the subsumptive and exhaustive perspective of a Hegelian absolute knowledge, the dialogue with tradition bears its own continual, intrinsic hermeneutic demands."), _c('a', {
    attrs: {
      "id": "rch1en101"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en101",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("101")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("First of all, the awareness entails seeing that the past is not a silent screen for our projections; it makes its own claims, and these inevitably confront our own prejudices. To actually engage with the past means to "), _c('a', {
    attrs: {
      "id": "page_38"
    }
  }), _vm._v("put these prejudices at risk and allow them to become questionable."), _c('a', {
    attrs: {
      "id": "rch1en102"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en102",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("102")])]), _vm._v(" In this acceptance of indeterminacy, what was previously called the fusion of horizons here takes the shape of a crossing-over or intermingling of questions."), _c('a', {
    attrs: {
      "id": "rch1en103"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en103",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("103")])]), _vm._v(" There are not only the questions that we pose to the past, but also the questions to which the claims of the past are themselves answers. To understand these latter questions means to pose them genuinely for ourselves."), _c('a', {
    attrs: {
      "id": "rch1en104"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en104",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("104")])]), _vm._v(" A further demand, then, is an openness of listening as opposed to a reconstruction of fixed meaning."), _c('a', {
    attrs: {
      "id": "rch1en105"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en105",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("105")])]), _vm._v(" Crucially, however, this listening and posing of questions does not entail agreement with whatever claims arise. The recognition of a question, after all, does not amount to an embrace of its traditional answer(s). Instead, the recognition requires a form of freedom: the ability to maintain enough distance to even hear the question, the openness of allowing something to become questionable to begin with."), _c('a', {
    attrs: {
      "id": "rch1en106"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en106",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("106")])]), _vm._v(" Any acknowledgment of authority that occurs in the crossing-over of questions, then, stems not from a blind deferral to the past,"), _c('a', {
    attrs: {
      "id": "rch1en107"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en107",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("107")])]), _vm._v(" but from an act of preservation based on resonance with the questions, a recognition of standards beyond one’s historical present."), _c('a', {
    attrs: {
      "id": "rch1en108"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en108",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("108")])]), _vm._v(" The past may pose its questions to us, and transmission intrinsically involves us in this dialogue, but it is we who, in understanding and living the questions, decide whether or not to keep asking them, thereby indicating – often only performatively – what constitutes a satisfactory answer.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("It is no coincidence that historically effected consciousness comes to evince a fundamentally conversational character. Even though this character arose here only through the analysis of the normative aspect of historical reflection, it is not difficult to see that it pervades the aspects of productivity and cognition as well. The various hermeneutic dimensions of historically effected consciousness thus appear to find their unity in a transmission that is historical as well as linguistic. This is hardly surprising since, as I mentioned at the outset of the chapter, the equiprimordial counterpart to the universality of historicity is that of linguisticality. Put differently, the transmission of tradition is nothing other than the ever-changing shape and texture of human community and communication."), _c('a', {
    attrs: {
      "id": "rch1en109"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en109",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("109")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The significance of this unity cannot be overstated. It reveals, on the one hand, that language cannot be separated from the historical core of "), _c('a', {
    attrs: {
      "id": "page_39"
    }
  }), _vm._v("Gadamer’s theory as a realm where his hermeneutical insights merely find application (as if the final third of "), _c('em', [_vm._v("Truth and Method")]), _vm._v(" were separable from the rest). On the other hand, it makes clear that historicity is not involved only with the historical or historiographical. Even a dismissive response to a given tradition or aspect thereof is a form of engagement that preserves transmission as such."), _c('a', {
    attrs: {
      "id": "rch1en110"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en110",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("110")])]), _vm._v(" All meaningful social involvement, furthermore, has an historical dimension, even if it does not engage with tradition as such or with a specific traditional content. This is a point that Gadamer himself lamented as remaining underdeveloped in the central sections of "), _c('em', [_vm._v("Truth and Method")]), _vm._v(". Given his emphasis there on textual encounter in the historical human sciences, it appeared that the general issues of distance and alterity lay beyond his purview, or in another area of hermeneutics."), _c('a', {
    attrs: {
      "id": "rch1en111"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en111",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("111")])]), _vm._v(" However, it is precisely this social and ethical dimension in which Gadamer takes the ultimate significance of historical effect to lie, and to which he subsequently dedicated more attention."), _c('a', {
    attrs: {
      "id": "rch1en112"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en112",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("112")])])]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("THE ETHICAL REGISTER")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Since my aim in the present work is to establish this significance, it will be necessary in the coming chapters to extend the analysis of historical effect to the structures and strata of social existence. I will conclude this chapter by transposing the discussion of historical positions from earlier sections into social categories. There, I noted that Gadamer’s position on historicality has been associated with traditionalism on the one hand, and on the other with a relativist perspectivism that asserts an incommensurability among historical epochs. Having analyzed Gadamer’s concept of historical effect, we are in a better position to see both how he navigates away from these positions, and how this shifts his philosophy into a social register.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Traditionalism, when viewed from a social perspective, amounts to a denial of alterity. It may admit that the other at first seems distant and foreign, but supposes that some process – be it the psychologism of romantic hermeneutics or the subsumption of Hegelian absolute mediation – is capable of fully uniting self and other. Since nothing and no one, therefore, is outside the reach of one’s appropriation, everything is "), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }), _vm._v("at least potentially familiar. To the incommensurabilist, the opposite is the case. The relativism of views is based on the assumption that there is no common experience, actual or potential, that could unite self and other. The two are separated by an insurmountable strangeness, which is held at bay either through a tolerance of the other’s position or through an elevation of the other to a position above the self."), _c('a', {
    attrs: {
      "id": "rch1en113"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en113",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("113")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("While proponents of each of these positions frequently accuse Gadamer of espousing the other, he in fact inhabits a position that eschews both."), _c('a', {
    attrs: {
      "id": "rch1en114"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en114",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("114")])]), _vm._v(" His is not exactly a mean position between the two, but some sort of middle stance is suggested by his assertion that “["), _c('em', [_vm._v("t")]), _vm._v("]"), _c('em', [_vm._v("he true locus of hermeneutics is")]), _vm._v(" [the] "), _c('em', [_vm._v("in-between")]), _vm._v("” of strangeness and familiarity."), _c('a', {
    attrs: {
      "id": "rch1en115"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en115",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("115")])]), _vm._v(" We saw this in-between in the intertwining of self and tradition, and can translate that here into a recognition of difference without utter division. The fusion of horizons entails a thorough mutual determination of self and other, such that neither exists completely in itself. The introduction of one great horizon is a way of formulating this relation negatively (“There are no closed horizons”"), _c('a', {
    attrs: {
      "id": "rch1en116"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en116",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("116")])]), _vm._v("), and does not involve a thorough appropriation on the part of the subject, as some have claimed."), _c('a', {
    attrs: {
      "id": "rch1en117"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en117",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("117")])]), _vm._v(" Gadamer certainly makes no effort to hide his indebtedness to romantic hermeneutics and Hegelian dialectic, but his defense of finitude supersedes their conclusions. While the ongoing movement of transmission involves mediation, our limitations, as well as the factual ruptures of history, preclude this from ever becoming absolute or complete.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("If Gadamer thus avoids the danger of totalizing essentialism, the openness that he identifies in historical effect does not, in turn, amount to a social indifference. To claim that human lives are incommensurable through and through would be, as Gadamer puts it, “a kind of Robinson Crusoe dream” that encloses each in his or her own selfhood."), _c('a', {
    attrs: {
      "id": "rch1en118"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en118",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("118")])]), _vm._v(" The supposed asymmetry between individuals is negated by our belonging to a shared life, however minimally that may be determined. That this life is perpetuated through the constant movement of transmission and the infinite iterations of dialogue only serves to strengthen the normative characterization of historical effect as an ongoing conversation possessing intrinsic demands. It should now be clear that these demands are not expressible as moral codes or social techniques (nor as any methodology in general), but lie rather in the responsive structure of historicity and "), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }), _vm._v("linguisticality themselves. One of the deepest demands, then, according to the openness and iterability of these structures, is the acknowledgement of an abyssal freedom, of the fact “that we live in a world that is greater than that which we can determine or control.”"), _c('a', {
    attrs: {
      "id": "rch1en119"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en119",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("119")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("While his overarching social stance may yet remain opaque, we can perhaps already see, in profile, how Gadamer conceives of the historical subject’s place in society. To be sure, it is a nonfoundational role that eschews the critical-theoretical ambitions of either side of historicist debates, but it also intends to avoid the “disappearance of the subject” in the overwhelming movement of historical epochs."), _c('a', {
    attrs: {
      "id": "rch1en120"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en120",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("120")])]), _vm._v(" It is, ultimately, a consciousness that recognizes both its boundness to tradition and the fact that “in tradition there is always an element of freedom.”"), _c('a', {
    attrs: {
      "id": "rch1en121"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en121",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("121")])]), _vm._v(" This chapter has given a glimpse of how Gadamer takes transmission to embody this freedom, in that it is at once a structural inheritance and a constant task, “both a presupposition and an achievement” of openness."), _c('a', {
    attrs: {
      "id": "rch1en122"
    }
  }), _c('sup', [_c('span', {
    attrs: {
      "href": "#/xhtml/notes#ch1en122",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("122")])]), _vm._v(" In subsequent chapters, I will amplify the analysis of this ambivalence, and develop further the theme of freedom within tradition.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-b87e6e88","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/chapter01.vue
// module id = 93
// module chunks = 0